// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import { CONSENT_MODES } from 'api/consent-modes/modes';

// Messages mirror the field-level validators they replace, so mounted inputs
// still show the same copy:
//   - pipeline variables: PipelineForm's `rules: { required: ... }`
//   - consent category:   ConsentCategorySelect's REQUIRED_MESSAGE
const VARIABLE_REQUIRED_MESSAGE = 'This field is required';
const CONSENT_CATEGORY_REQUIRED_MESSAGE = 'Required field';

export const isBlank = value => value === undefined || value === null || value === '';

// The one place this feature decides "external mode forbids a category". Shared
// with applyBulkConsent so the rule can't drift between validation and bulk-apply.
export const isExternalMode = mode => mode === CONSENT_MODES.external;

/**
 * The variable names a playbook requires, de-duplicated. Mirrors the derivation
 * in ConfirmUpdateContent so seeding and validation agree on the field set.
 */
export function getRequiredVariableNames(targetPlaybook) {
  return [...new Set(targetPlaybook?.pipeline_variables?.map(variable => variable.name) || [])];
}

/**
 * The single source of truth for the per-field completeness checks. Both the
 * resolver (which reports WHICH field is wrong) and isPipelineComplete (which
 * reports the boolean) are built from these primitives so they can never
 * disagree.
 */
const getMissingRequiredVariableNames = (pipelineValues, requiredVariableNames) =>
  requiredVariableNames.filter(name => isBlank(pipelineValues[name]));

const isConsentCategoryMissing = (pipelineValues, consentCategoryEnabled) =>
  consentCategoryEnabled && !isExternalMode(pipelineValues.consentMode) && isBlank(pipelineValues.consentCategoryId);

/**
 * Is a single pipeline's seeded values complete? A pipeline is complete iff
 * every required variable is non-blank AND the consent category is present when
 * required (consent enabled, mode not external). Shares its checks with the
 * resolver so the boolean and the field-level errors always agree.
 */
export function isPipelineComplete(pipelineValues = {}, { requiredVariableNames, consentCategoryEnabled }) {
  if (getMissingRequiredVariableNames(pipelineValues, requiredVariableNames).length) {
    return false;
  }
  if (isConsentCategoryMissing(pipelineValues, consentCategoryEnabled)) {
    return false;
  }
  return true;
}

/**
 * The ids of the pipelines that fail the completeness check, in the order they
 * are listed in pipelineIds. Powers the "needs attention" column and filter.
 */
export function getIncompletePipelineIds(values, { pipelineIds, requiredVariableNames, consentCategoryEnabled }) {
  return pipelineIds.filter(
    id => !isPipelineComplete(values?.[id] || {}, { requiredVariableNames, consentCategoryEnabled })
  );
}

/**
 * Seed react-hook-form's defaultValues for EVERY selected pipeline up front, so
 * validity reflects the whole selection even when most rows are never mounted
 * (pagination/virtualization). Consent fields are seeded only when their
 * respective feature flag is on — seeding consentMode unconditionally would make
 * buildConsentVars emit a consent row for orgs that have consent disabled.
 */
export function buildConfirmUpdateDefaultValues({
  selectedPipelines,
  targetPlaybook,
  integrationId,
  consentModeEnabled,
  consentCategoryEnabled,
}) {
  const variableNames = getRequiredVariableNames(targetPlaybook);

  return selectedPipelines.reduce((values, pipeline) => {
    // 'revision' is the alias for staged_revision (see useLazyQueryIonPipelines).
    const existing = pipeline.pipelineIntegrations?.find(
      pipelineIntegration => pipelineIntegration.integrationId === integrationId
    );
    const existingVars = existing?.revision?.pipelineVars || {};

    const pipelineValues = {};
    variableNames.forEach(name => {
      pipelineValues[name] = existingVars[name] || '';
    });

    if (consentModeEnabled) {
      pipelineValues.consentMode = existing?.consent?.mode ?? CONSENT_MODES.ace;
    }
    if (consentCategoryEnabled) {
      pipelineValues.consentCategoryId = existing?.consent?.consentCategoryId || '';
    }

    values[pipeline.id] = pipelineValues;
    return values;
  }, {});
}

/**
 * Build a react-hook-form resolver that validates the ENTIRE selection from the
 * form values (not from mounted fields). This is what lets isValid stay correct
 * when rows are paginated/virtualized off-screen. Because a resolver supersedes
 * all field-level `rules`, it owns every check the inputs used to do:
 *   - each required pipeline variable is non-empty
 *   - consent category (when enabled) is set unless the mode is external
 * Errors are nested by `[pipelineId][field]` to match how the inputs read them.
 */
export function makeConfirmUpdateResolver({ pipelineIds, requiredVariableNames, consentCategoryEnabled }) {
  return values => {
    const errors = {};

    const addError = (pipelineId, field, message) => {
      errors[pipelineId] = errors[pipelineId] || {};
      errors[pipelineId][field] = { type: 'required', message };
    };

    pipelineIds.forEach(pipelineId => {
      const pipelineValues = values?.[pipelineId] || {};

      getMissingRequiredVariableNames(pipelineValues, requiredVariableNames).forEach(name => {
        addError(pipelineId, name, VARIABLE_REQUIRED_MESSAGE);
      });

      if (isConsentCategoryMissing(pipelineValues, consentCategoryEnabled)) {
        addError(pipelineId, 'consentCategoryId', CONSENT_CATEGORY_REQUIRED_MESSAGE);
      }
    });

    return { values: Object.keys(errors).length ? {} : values, errors };
  };
}
